import { ChevronDown, Eye, MousePointer, Percent, Target, TrendingUp } from 'lucide-react'
import { useState } from 'react'
import { SiGoogleanalytics } from 'react-icons/si'
import type { NodeExecution } from 'sprites-types/api/v2/workflows'
import { getToolResultSchema } from 'sprites-types/workflow/tools/common'
import {
  googleSearchConsolePerformanceMetrics,
  type GoogleSearchConsolePerformanceMetricsResult,
} from 'sprites-types/workflow/tools/googleSearchConsolePerformanceMetrics'
import {
  createWorkflowToolComponent,
  type WorkflowToolComponentProps,
} from '../shared/WorkflowToolComponent'
import { SchemaValidationError } from '../shared/safeParseToolResult'

function GoogleSearchConsolePerformanceMetricsDisplayComponent({
  nodeExecution,
}: WorkflowToolComponentProps) {
  const [isExpanded, setIsExpanded] = useState(true)
  const [showAllRows, setShowAllRows] = useState(false)
  const schema = getToolResultSchema(googleSearchConsolePerformanceMetrics, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return <SchemaValidationError error={parseResult.error} nodeExecution={nodeExecution} />
  }

  const result = parseResult.data

  // Handle both array and object result formats
  const getResultData = (): GoogleSearchConsolePerformanceMetricsResult => {
    if (Array.isArray(result)) {
      return result[0]
    }
    return result
  }

  const resultData = getResultData()
  const rows = resultData?.rows || []
  const siteUrl = nodeExecution.inputs?.siteUrl || 'Unknown Site'
  const startDate = nodeExecution.inputs?.startDate || ''
  const endDate = nodeExecution.inputs?.endDate || ''
  const dimensions = nodeExecution.inputs?.dimensions || []

  // Calculate totals
  const totals = rows.reduce(
    (acc, row) => {
      acc.clicks += row.clicks || 0
      acc.impressions += row.impressions || 0
      acc.ctr += row.ctr || 0
      acc.position += row.position || 0
      return acc
    },
    { clicks: 0, impressions: 0, ctr: 0, position: 0 }
  )

  const avgCtr = rows.length > 0 ? totals.ctr / rows.length : 0
  const avgPosition = rows.length > 0 ? totals.position / rows.length : 0

  const formatNumber = (num: number): string => {
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(2)}M`
    }
    if (num >= 1000) {
      return `${(num / 1000).toFixed(2)}K`
    }
    return num.toFixed(0)
  }

  const formatPercentage = (num: number): string => {
    return `${(num * 100).toFixed(2)}%`
  }

  const formatPosition = (num: number): string => {
    return num.toFixed(1)
  }

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="rounded-3xl border border-gray-200/50 bg-white/70 text-black shadow-xl backdrop-blur-[8px]">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="group flex w-full items-center gap-4 px-4 py-3 text-left transition-colors hover:bg-gray-50/50"
          aria-expanded={isExpanded}
        >
          <div className="flex-shrink-0">
            <SiGoogleanalytics className="h-5 w-5 text-[#4285f4]" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-gray-900">
              Google Search Console Performance
            </div>
            <div className="text-xs font-medium tracking-wide text-gray-500">
              {rows.length} keywords • {formatNumber(totals.impressions)} impressions
            </div>
            <div className="truncate text-xs text-gray-600">{siteUrl}</div>
            {(startDate || endDate) && (
              <div className="text-xs text-gray-500">
                {startDate} to {endDate}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <div className="rounded-full bg-blue-100 px-3 py-1 text-xs font-semibold text-blue-600">
              {rows.length} Keywords
            </div>
            <ChevronDown
              className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
              aria-hidden="true"
            />
          </div>
        </button>

        {isExpanded && (
          <div className="border-t border-gray-200/50 px-4 py-4">
            {/* Performance Statistics */}
            <div className="mb-6">
              <div className="mb-4 flex items-center gap-2">
                <div className="text-sm font-bold text-gray-900">Performance Statistics</div>
                <div className="h-px flex-1 bg-gray-200"></div>
              </div>

              {/* Date Range and Keywords Info */}
              <div className="mb-4 flex flex-wrap gap-2">
                {startDate && endDate && (
                  <div className="rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-700">
                    {startDate} to {endDate}
                  </div>
                )}
                <div className="rounded-full bg-blue-100 px-3 py-1 text-xs font-medium text-blue-700">
                  {rows.length} Keyword{rows.length !== 1 ? 's' : ''}
                </div>
                {dimensions.length > 0 && (
                  <div className="rounded-full bg-purple-100 px-3 py-1 text-xs font-medium text-purple-700">
                    Grouped by {dimensions.join(', ')}
                  </div>
                )}
              </div>

              {/* Key Metrics Grid - Ordered by priority */}
              <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                {/* PRIORITY 1: CLICKS */}
                <div className="rounded-2xl border-2 border-blue-200 bg-gradient-to-br from-blue-50 to-blue-100/50 p-4 shadow-md shadow-blue-100/50">
                  <div className="mb-2 flex items-center gap-2">
                    <MousePointer className="h-4 w-4 text-blue-600" />
                    <span className="text-xs font-medium text-gray-700">Total Clicks</span>
                  </div>
                  <div className="text-lg font-bold text-blue-700">
                    {formatNumber(totals.clicks)}
                  </div>
                </div>

                {/* PRIORITY 2: IMPRESSIONS */}
                <div className="rounded-2xl border-2 border-green-200 bg-gradient-to-br from-green-50 to-green-100/50 p-4 shadow-md shadow-green-100/50">
                  <div className="mb-2 flex items-center gap-2">
                    <Eye className="h-4 w-4 text-green-600" />
                    <span className="text-xs font-medium text-gray-700">Total Impressions</span>
                  </div>
                  <div className="text-lg font-bold text-green-700">
                    {formatNumber(totals.impressions)}
                  </div>
                </div>

                {/* PRIORITY 3: CTR */}
                <div className="rounded-2xl border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-purple-100/50 p-4 shadow-md shadow-purple-100/50">
                  <div className="mb-2 flex items-center gap-2">
                    <Percent className="h-4 w-4 text-purple-600" />
                    <span className="text-xs font-medium text-gray-700">Avg CTR</span>
                  </div>
                  <div className="text-lg font-bold text-purple-700">
                    {formatPercentage(avgCtr)}
                  </div>
                </div>

                {/* PRIORITY 4: POSITION */}
                <div className="rounded-2xl border-2 border-orange-200 bg-gradient-to-br from-orange-50 to-orange-100/50 p-4 shadow-md shadow-orange-100/50">
                  <div className="mb-2 flex items-center gap-2">
                    <Target className="h-4 w-4 text-orange-600" />
                    <span className="text-xs font-medium text-gray-700">Avg Position</span>
                  </div>
                  <div className="text-lg font-bold text-orange-700">
                    {formatPosition(avgPosition)}
                  </div>
                </div>
              </div>
            </div>

            {/* Performance Data Section */}
            {rows.length > 0 ? (
              <div className="mb-6">
                <div className="mb-4 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="h-4 w-4 text-gray-600" />
                    <div className="text-sm font-bold text-gray-900">Keyword Performance</div>
                  </div>
                  {rows.length > 10 && (
                    <button
                      onClick={() => setShowAllRows(!showAllRows)}
                      className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50"
                    >
                      {showAllRows ? `Show Top 10` : `Show All ${rows.length}`}
                    </button>
                  )}
                </div>

                <div className="overflow-hidden rounded-2xl border border-gray-200/60 bg-white shadow-sm">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-50/50">
                          {(dimensions.length > 0 ||
                            rows.some((row) => row.keys && row.keys.length > 0)) && (
                            <th className="px-4 py-3 text-left text-xs font-bold text-gray-700">
                              {dimensions.length > 0 ? dimensions.join(' / ') : 'Keywords'}
                            </th>
                          )}
                          <th className="px-4 py-3 text-right text-xs font-bold text-blue-700">
                            <div className="flex items-center justify-end gap-1">
                              <MousePointer className="h-3 w-3" />
                              Clicks
                            </div>
                          </th>
                          <th className="px-4 py-3 text-right text-xs font-bold text-green-700">
                            <div className="flex items-center justify-end gap-1">
                              <Eye className="h-3 w-3" />
                              Impressions
                            </div>
                          </th>
                          <th className="px-4 py-3 text-right text-xs font-bold text-purple-700">
                            <div className="flex items-center justify-end gap-1">
                              <Percent className="h-3 w-3" />
                              CTR
                            </div>
                          </th>
                          <th className="px-4 py-3 text-right text-xs font-bold text-orange-700">
                            <div className="flex items-center justify-end gap-1">
                              <Target className="h-3 w-3" />
                              Position
                            </div>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {(showAllRows ? rows : rows.slice(0, 10)).map((row, idx) => (
                          <tr
                            key={idx}
                            className="border-b border-gray-100 transition-colors hover:bg-gradient-to-r hover:from-blue-50/20 hover:to-green-50/20"
                          >
                            {(dimensions.length > 0 ||
                              rows.some((row) => row.keys && row.keys.length > 0)) && (
                              <td className="px-4 py-3 text-xs">
                                {row.keys && row.keys.length > 0 ? (
                                  <div className="flex flex-col gap-1">
                                    {row.keys.map((key, keyIdx) => (
                                      <span
                                        key={keyIdx}
                                        className="max-w-xs truncate font-medium text-gray-900"
                                      >
                                        {dimensions.length > 0 && keyIdx < dimensions.length ? (
                                          <span>
                                            <span className="text-xs text-gray-500">
                                              {dimensions[keyIdx]}:
                                            </span>{' '}
                                            {key || 'N/A'}
                                          </span>
                                        ) : (
                                          key || 'N/A'
                                        )}
                                      </span>
                                    ))}
                                  </div>
                                ) : (
                                  <span className="text-gray-500">N/A</span>
                                )}
                              </td>
                            )}
                            <td className="px-4 py-3 text-right">
                              <div className="font-bold text-blue-700">
                                {formatNumber(row.clicks)}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-right">
                              <div className="font-bold text-green-700">
                                {formatNumber(row.impressions)}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-right">
                              <div className="font-bold text-purple-700">
                                {formatPercentage(row.ctr)}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-right">
                              <div
                                className={`font-bold ${
                                  row.position <= 3
                                    ? 'text-green-600'
                                    : row.position <= 10
                                      ? 'text-orange-600'
                                      : 'text-red-600'
                                }`}
                              >
                                {formatPosition(row.position)}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {!showAllRows && rows.length > 10 && (
                    <div className="border-t border-gray-100 bg-gray-50/50 px-4 py-2 text-center">
                      <button
                        onClick={() => setShowAllRows(true)}
                        className="text-xs font-medium text-blue-600 hover:text-blue-700"
                      >
                        Show {rows.length - 10} more keywords
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="mb-6 overflow-hidden rounded-2xl border-2 border-gray-200 bg-gradient-to-br from-gray-50 to-gray-100/50 p-8 text-center shadow-sm">
                <div className="text-sm text-gray-600">
                  No performance data found for this period
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

function downloadGoogleSearchConsolePerformanceMetrics(
  _nodeExecution: NodeExecution
): Promise<void> {
  return Promise.resolve()
}

function googleSearchConsolePerformanceMetricsToMarkdown(nodeExecution: NodeExecution): string {
  const schema = getToolResultSchema(googleSearchConsolePerformanceMetrics, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return `## Google Search Console Performance Metrics\n\nError: Invalid result format`
  }

  const result = parseResult.data

  const getResultData = (): GoogleSearchConsolePerformanceMetricsResult => {
    if (Array.isArray(result)) {
      return result[0]
    }
    return result
  }

  const resultData = getResultData()
  const rows = resultData?.rows || []
  const siteUrl = nodeExecution.inputs?.siteUrl || 'Unknown Site'
  const startDate = nodeExecution.inputs?.startDate || ''
  const endDate = nodeExecution.inputs?.endDate || ''
  const dimensions = nodeExecution.inputs?.dimensions || []

  const buildMarkdown = (): string => {
    const sections: string[] = [
      `## Google Search Console Performance Metrics\n\n`,
      `**Site:** ${siteUrl}\n`,
    ]

    if (startDate && endDate) {
      sections.push(`**Date Range:** ${startDate} to ${endDate}\n`)
    }

    if (dimensions.length > 0) {
      sections.push(`**Grouped By:** ${dimensions.join(', ')}\n`)
    }

    sections.push(`\n**Total Rows:** ${rows.length}\n\n`)

    // Calculate totals
    const totals = rows.reduce(
      (acc, row) => {
        acc.clicks += row.clicks || 0
        acc.impressions += row.impressions || 0
        acc.ctr += row.ctr || 0
        acc.position += row.position || 0
        return acc
      },
      { clicks: 0, impressions: 0, ctr: 0, position: 0 }
    )

    const avgCtr = rows.length > 0 ? totals.ctr / rows.length : 0
    const avgPosition = rows.length > 0 ? totals.position / rows.length : 0

    sections.push(`### Summary\n\n`)
    sections.push(`- **Total Clicks:** ${totals.clicks.toLocaleString()}\n`)
    sections.push(`- **Total Impressions:** ${totals.impressions.toLocaleString()}\n`)
    sections.push(`- **Average CTR:** ${(avgCtr * 100).toFixed(2)}%\n`)
    sections.push(`- **Average Position:** ${avgPosition.toFixed(1)}\n\n`)

    if (rows.length > 0) {
      sections.push(`### Performance Data\n\n`)

      // Check if any rows have keys data
      const hasKeyData = rows.some((row) => row.keys && row.keys.length > 0)

      sections.push(`| `)
      if (dimensions.length > 0 || hasKeyData) {
        if (dimensions.length > 0) {
          sections.push(`${dimensions.join(' | ')} | `)
        } else {
          sections.push(`Keywords/Dimensions | `)
        }
      }
      sections.push(`Clicks | Impressions | CTR | Position |\n`)
      sections.push(`|`)
      if (dimensions.length > 0 || hasKeyData) {
        if (dimensions.length > 0) {
          dimensions.forEach(() => sections.push(` --- |`))
        } else {
          sections.push(` --- |`)
        }
      }
      sections.push(` --- | --- | --- | --- |\n`)

      rows.slice(0, 100).forEach((row) => {
        sections.push(`| `)
        if (dimensions.length > 0 || hasKeyData) {
          if (row.keys && row.keys.length > 0) {
            row.keys.forEach((key, keyIdx) => {
              if (dimensions.length > 0 && keyIdx < dimensions.length) {
                sections.push(`${dimensions[keyIdx]}: ${key || 'N/A'} | `)
              } else {
                sections.push(`${key || 'N/A'} | `)
              }
            })
          } else {
            sections.push(`N/A | `)
          }
        }
        sections.push(
          `${row.clicks.toLocaleString()} | ${row.impressions.toLocaleString()} | ${(row.ctr * 100).toFixed(2)}% | ${row.position.toFixed(1)} |\n`
        )
      })

      if (rows.length > 100) {
        sections.push(`\n*Showing first 100 of ${rows.length} rows*\n`)
      }
    }

    sections.push(`\n---\n`)

    return sections.join('')
  }

  return buildMarkdown()
}

export const GoogleSearchConsolePerformanceMetricsDisplay = createWorkflowToolComponent(
  GoogleSearchConsolePerformanceMetricsDisplayComponent,
  downloadGoogleSearchConsolePerformanceMetrics,
  googleSearchConsolePerformanceMetricsToMarkdown
)
